<mat-toolbar color="accent">
  {{ isPdfMode ? 'Generate PDF' : 'Print Settings' }}
</mat-toolbar>

<mat-dialog-content>
  <pr-system-template-selectors
    [templates]="data.templates"
    (templateChange)="selectedTemplates$.next($event)"
  />

  @if (isPdfMode) {
    <mat-form-field class="mt-3 w-full">
      <mat-label>File Name</mat-label>
      <input matInput [formControl]="fileNameControl" />
      <span matSuffix class="pr-3">.pdf</span>
    </mat-form-field>
  }

  <div class="mt-4 flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <h2>Print Overrides</h2>
      <button
        type="button"
        mat-stroked-button
        class="!text-xs"
        (click)="resetToDefaults()"
      >
        <mat-icon class="icon-sm">refresh</mat-icon>
        Reset to Defaults
      </button>
    </div>

    <pr-information-box>
      These settings apply to this print only and won't be saved.
    </pr-information-box>

    <div class="mt-2 grid grid-cols-2 gap-3">
      <mat-form-field
        class="w-full"
        subscriptSizing="dynamic"
        floatLabel="always"
      >
        <mat-label>Font Family</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.fontFamily"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (font of curatedFonts; track font.fontFamily) {
            <mat-option [value]="font.fontFamily">
              <span [style.font-family]="font.fontFamily">
                {{ font.name }}
              </span>
            </mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field subscriptSizing="dynamic" floatLabel="always">
        <mat-label>Font Size</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.fontSize"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (size of fontSizePresets; track size) {
            <mat-option [value]="size">{{ size }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field subscriptSizing="dynamic" floatLabel="always">
        <mat-label>Line Height</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.lineHeight"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (height of lineHeightPresets; track height) {
            <mat-option [value]="height">{{ height }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field subscriptSizing="dynamic" floatLabel="always">
        <mat-label>Paragraph Spacing</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.paragraphSpacing"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (spacing of paragraphSpacingPresets; track spacing) {
            <mat-option [value]="spacing">{{ spacing }}</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    <p class="text-xs text-gray-500">Margins (mm)</p>
    <div class="grid grid-cols-2 gap-3">
      <mat-form-field subscriptSizing="dynamic" floatLabel="always">
        <mat-label>Top</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.marginTop"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (margin of marginPresets; track margin) {
            <mat-option [value]="margin">{{ margin }}mm</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field subscriptSizing="dynamic" floatLabel="always">
        <mat-label>Bottom</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.marginBottom"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (margin of marginPresets; track margin) {
            <mat-option [value]="margin">{{ margin }}mm</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field subscriptSizing="dynamic" floatLabel="always">
        <mat-label>Left</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.marginLeft"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (margin of marginPresets; track margin) {
            <mat-option [value]="margin">{{ margin }}mm</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field subscriptSizing="dynamic" floatLabel="always">
        <mat-label>Right</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.marginRight"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (margin of marginPresets; track margin) {
            <mat-option [value]="margin">{{ margin }}mm</mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>

    <div class="grid grid-cols-2 gap-3">
      <mat-form-field subscriptSizing="dynamic" floatLabel="always">
        <mat-label>Paper Size</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.paperSize"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (size of paperSizePresets; track size) {
            <mat-option [value]="size">{{ size }}</mat-option>
          }
        </mat-select>
      </mat-form-field>

      <mat-form-field subscriptSizing="dynamic" floatLabel="always">
        <mat-label>Orientation</mat-label>
        <mat-select
          placeholder="Default"
          [formControl]="documentSettingsForm.controls.orientation"
        >
          <mat-option [value]="undefined">Default</mat-option>
          @for (orient of orientationPresets; track orient) {
            <mat-option [value]="orient">
              {{ orient | titlecase }}
            </mat-option>
          }
        </mat-select>
      </mat-form-field>
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions>
  <pt-buttons-container padding="none">
    <button mat-stroked-button (click)="close()">Cancel</button>

    @if (!isPdfMode) {
      <button mat-flat-button color="primary" (click)="print()">Print</button>
    }
    @if (isPdfMode) {
      <pt-button-with-menu
        label="Download PDF"
        type="flat"
        color="primary"
        (buttonClick)="downloadPdf()"
      >
        <button mat-menu-item (click)="saveToPatient()">
          <mat-icon>save</mat-icon>
          Save to Patient Files
        </button>
      </pt-button-with-menu>
    }
  </pt-buttons-container>
</mat-dialog-actions>
